import { useEffect, useRef, useState } from 'react'

import { get } from '../lib/api'
import { useResource } from '../lib/useResource'
import { Notice } from './ui'

/**
 * Where one emergency dispatch was raised from.
 *
 * Google Maps' JavaScript API, loaded on demand rather than in `index.html`.
 * Two reasons: the console is mostly screens that never show a map, and
 * loading a third-party script on every page — including the login page —
 * would put an outbound request on an origin that renders PHI before anyone
 * has even signed in.
 *
 * The key comes from `/admin/config/console`, not from the build. It is a
 * publishable browser key — the right kind to hand a browser — and serving it
 * at runtime means it can be rotated from the console that uses it rather than
 * by rebuilding and redeploying. `VITE_GOOGLE_MAPS_KEY` stays as a fallback so
 * an existing deployment keeps working until somebody sets one in the console.
 *
 * It must be restricted by HTTP referrer in the Google Cloud console. An
 * unrestricted browser key is one anybody can lift and spend.
 *
 * With no key at all this renders the coordinates and says so, rather than an
 * empty grey box. An operator who needs to send an ambulance can still read
 * the numbers into a phone.
 */

const BUILD_KEY = import.meta.env.VITE_GOOGLE_MAPS_KEY ?? ''

type MapsWindow = Window & {
  google?: { maps?: unknown }
  __seyonMapsPromise?: Promise<void>
}

/** Loads the Maps script once per page, however many maps ask for it. */
function loadMaps(key: string): Promise<void> {
  const w = window as MapsWindow
  if (w.google?.maps) return Promise.resolve()
  if (w.__seyonMapsPromise) return w.__seyonMapsPromise

  w.__seyonMapsPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=marker&loading=async`
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('The Google Maps script could not be loaded'))
    document.head.appendChild(script)
  })
  return w.__seyonMapsPromise
}

/** Dark map styling, so the map does not glare out of a dark console. */
const DARK_STYLE = [
  { elementType: 'geometry', stylers: [{ color: '#0d0d11' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#0d0d11' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8a8a95' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#1c1c22' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#6b6b76' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#08080b' }] },
]

export type DispatchLocation = {
  latitude: number
  longitude: number
  accuracy_m?: number | null
}

export function DispatchMap({ location, height = 260 }: { location: DispatchLocation; height?: number }) {
  const container = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)

  // The console's own key, set from the Sign-in & keys screen. Falls back to
  // the build-time value so a deployment that already has one keeps working.
  const config = useResource((signal) =>
    get<{ maps_browser_key: string }>('/v1/admin/config/console', undefined, signal),
  )
  const key = config.data?.maps_browser_key || BUILD_KEY

  useEffect(() => {
    if (!key) return
    let cancelled = false

    void (async () => {
      try {
        await loadMaps(key)
        if (cancelled || !container.current) return

        // Typed loosely on purpose: pulling in `@types/google.maps` for one
        // screen is a dependency for a handful of property names.
        const maps = (window as MapsWindow).google!.maps as any
        const centre = { lat: location.latitude, lng: location.longitude }
        const dark = document.documentElement.dataset.theme !== 'light'

        const map = new maps.Map(container.current, {
          center: centre,
          zoom: 16,
          disableDefaultUI: true,
          zoomControl: true,
          styles: dark ? DARK_STYLE : undefined,
        })

        new maps.Marker({
          position: centre,
          map,
          title: 'Where the dispatch was raised',
        })

        // The accuracy circle is the honest part of the picture. A pin alone
        // reads as "they were here"; a 400-metre radius reads as "somewhere in
        // these streets", which is what a phone fix from indoors actually
        // means — and it is the difference between searching a building and
        // searching a block.
        if (location.accuracy_m && location.accuracy_m > 0) {
          new maps.Circle({
            map,
            center: centre,
            radius: location.accuracy_m,
            strokeColor: '#00E58A',
            strokeOpacity: 0.5,
            strokeWeight: 1,
            fillColor: '#00E58A',
            fillOpacity: 0.1,
          })
        }
      } catch (caught) {
        if (!cancelled) {
          setError(caught instanceof Error ? caught.message : String(caught))
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [key, location.latitude, location.longitude, location.accuracy_m])

  const coordinates = `${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}`

  // Still fetching — say nothing yet rather than flashing "not configured" at
  // somebody whose key is about to arrive.
  if (config.loading && !config.data && !BUILD_KEY) {
    return <div style={{ height }} />
  }

  if (!key || error) {
    return (
      <div className="stack">
        <Notice tone="warn">
          {error ??
            'No Google Maps key is set. Add a browser key under Sign-in & keys to draw the map here.'}
        </Notice>
        <div>
          <div className="dim" style={{ fontSize: 11.5, marginBottom: 4 }}>
            Coordinates
          </div>
          <div className="mono" style={{ fontSize: 14 }}>
            {coordinates}
          </div>
          {location.accuracy_m ? (
            <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
              accurate to about {Math.round(location.accuracy_m)} m
            </div>
          ) : null}
        </div>
        <div>
          <a
            className="btn btn--sm"
            href={`https://www.google.com/maps/search/?api=1&query=${location.latitude},${location.longitude}`}
            target="_blank"
            rel="noreferrer"
          >
            Open in Google Maps
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="stack" style={{ gap: 8 }}>
      <div
        ref={container}
        style={{ height, borderRadius: 12, overflow: 'hidden', background: 'var(--surface-alt)' }}
        role="img"
        aria-label={`Map showing the dispatch location, ${coordinates}`}
      />
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span className="dim mono" style={{ fontSize: 11 }}>
          {coordinates}
          {location.accuracy_m ? ` · ±${Math.round(location.accuracy_m)} m` : ''}
        </span>
        <a
          className="dim"
          style={{ fontSize: 11 }}
          href={`https://www.google.com/maps/search/?api=1&query=${location.latitude},${location.longitude}`}
          target="_blank"
          rel="noreferrer"
        >
          Open in Google Maps
        </a>
      </div>
    </div>
  )
}
